/**
 * Notification Preview Component
 * 
 * Shows what notifications will look like before enabling
 */

import { Card } from './ui/card';
import { Bell, Clock, Zap, TrendingUp, Users, Calendar } from 'lucide-react';

interface NotificationPreviewProps {
  type?: 'checkin' | 'streak' | 'partner' | 'tension' | 'weekly' | 'action';
}

export function NotificationPreview({ type = 'checkin' }: NotificationPreviewProps) {
  const notifications = {
    checkin: {
      icon: Clock,
      title: 'Daily Check-In Time',
      body: 'Take 2 minutes to track your relationship tension. Consistency makes your forecast more accurate.',
      time: '8:00 PM',
    },
    streak: {
      icon: Zap,
      title: '🔥 7-Day Streak!',
      body: 'One week of consistent check-ins. Your RTI forecast is getting sharper.',
      time: 'Just now',
    },
    partner: {
      icon: Users,
      title: 'Partner Check-In Complete',
      body: 'Sarah completed today\'s check-in. Your turn!',
      time: '5 minutes ago',
    },
    tension: {
      icon: TrendingUp,
      title: 'High Tension Detected',
      body: 'Your RTI is 68. Today\'s action card recommends restraint and space.',
      time: 'Just now',
    },
    weekly: {
      icon: Calendar,
      title: 'Weekly Summary Ready',
      body: 'Review your relationship patterns from the past week.',
      time: 'Sunday, 10:00 AM',
    },
    action: {
      icon: Zap,
      title: 'Daily Action Reminder',
      body: 'Today\'s action: Practice active listening during your next conversation',
      time: '2:00 PM',
    },
  };

  const notification = notifications[type];
  const Icon = notification.icon;

  return (
    <Card className="p-4 bg-white border-2 border-blue-200 shadow-lg max-w-sm">
      {/* Browser notification chrome */}
      <div className="flex items-start gap-3">
        {/* App icon */}
        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          {/* App name and time */}
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-neutral-900">TENSION</p>
            <p className="text-xs text-neutral-500">{notification.time}</p>
          </div>

          {/* Notification content */}
          <div className="space-y-1">
            <p className="text-sm font-medium text-neutral-900 leading-tight">
              {notification.title}
            </p>
            <p className="text-xs text-neutral-700 leading-relaxed">
              {notification.body}
            </p>
          </div>
        </div>

        {/* Notification icon */}
        <div className="flex-shrink-0">
          <Icon className="w-4 h-4 text-neutral-400" />
        </div>
      </div>
    </Card>
  );
}

/**
 * Notification Gallery - Shows all notification types
 */
export function NotificationGallery() {
  const types: Array<{ id: any; label: string }> = [
    { id: 'checkin', label: 'Daily Check-In' },
    { id: 'streak', label: 'Streak Milestone' },
    { id: 'partner', label: 'Partner Sync' },
    { id: 'tension', label: 'High Tension' },
    { id: 'weekly', label: 'Weekly Summary' },
    { id: 'action', label: 'Action Reminder' },
  ];

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div>
          <h3 className="font-medium mb-1">Notification Examples</h3>
          <p className="text-sm text-neutral-600">Here's what you'll receive</p>
        </div>

        <div className="space-y-4">
          {types.map(type => (
            <div key={type.id} className="space-y-2">
              <p className="text-xs font-medium text-neutral-700">{type.label}</p>
              <NotificationPreview type={type.id as any} />
            </div>
          ))}
        </div>

        <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <p className="text-xs text-neutral-600">
            These are examples. Actual notifications will be personalized based on your 
            relationship data and check-in patterns.
          </p>
        </div>
      </div>
    </Card>
  );
}
